import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Stack, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGridScrollToBottom } from "../../../core/hooks/useGridScrollToBottom";
import { useResponsive } from "../../../core/hooks/useResponsive";
import OrderBookCard from "./OrderBookCard";
import OrderLogDialog from "./orderLogDialog";

const OrderBookGrid = ({ orderBook }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isCompact } = useResponsive();

  useGridScrollToBottom(orderBook, "order-book-grid");

  const handleViewLogClick = (row) => {
    setSelectedOrder(row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const orderBookColumns = [
    { field: "symbol", headerName: "Symbol", flex: 1 },
    {
      field: "orderPlaced",
      headerName: "Order Placed",
      flex: 1,
      type: "boolean",
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "orderExecuted",
      headerName: "Order Executed",
      flex: 1,
      type: "boolean",
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    {
      field: "orderLog",
      headerName: "Order Log",
      flex: 2,
      renderCell: (params) => {
        const hasLog = params.value && Array.isArray(params.value) && params.value.length > 0;
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewLogClick(params.row);
            }}
            disabled={!hasLog}
            sx={{
              color: hasLog ? "primary.main" : "action.disabled",
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  if (isCompact) {
    // "Order Placed" / "Order Executed" headers alone need more width than a phone
    // has, so the row becomes a card with the same log button.
    const rows = orderBook || [];
    return (
      <>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No orders yet.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ pr: 0.25 }}>
            {rows.map((row, i) => (
              <OrderBookCard key={row.entryId ?? i} row={row} onViewLog={handleViewLogClick} />
            ))}
          </Stack>
        )}
        <OrderLogDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          orderLog={selectedOrder?.orderLog || []}
          symbol={selectedOrder?.symbol}
        />
      </>
    );
  }

  return (
    <>
      <div id="order-book-grid" style={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={orderBook || []}
          columns={orderBookColumns}
          getRowId={(row) => row.entryId ?? `${row.tradeId ?? row.token}-${row.orderId ?? ""}`}
          rowHeight={35}
          hideFooter
          sx={{ height: "100%" }}
        />
      </div>
      <OrderLogDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        orderLog={selectedOrder?.orderLog || []}
        symbol={selectedOrder?.symbol}
      />
    </>
  );
};

export default OrderBookGrid;
